import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API } from '../App';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [language, setLanguage] = useState('de');
  const [resending, setResending] = useState(false);
  const verificationAttempted = useRef(false);

  useEffect(() => {
    // Check for user preference in localStorage, then browser language, default to German
    const storedUser = localStorage.getItem('user');
    let lang = 'de'; // Default to German
    
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        lang = user.language || 'de';
      } catch (e) {
        // If parsing fails, check browser language
        lang = navigator.language?.startsWith('de') ? 'de' : 'en';
      }
    } else {
      // No stored user, check browser language, default to German
      lang = navigator.language?.startsWith('de') ? 'de' : 
             navigator.language?.startsWith('en') ? 'en' : 'de';
    }
    
    setLanguage(lang);

    const verifyEmail = async () => {
      // Prevent multiple verification attempts
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        return;
      }

      try {
        await axios.get(`${API}/auth/verify-email?token=${token}`);
        setStatus('success');
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      // Redirect to login page where user can resend verification
      navigate('/auth');
      toast.info(language === 'de' 
        ? 'Bitte melden Sie sich an, um eine neue Bestätigungs-E-Mail zu erhalten.' 
        : 'Please log in to request a new verification email.');
    } catch (error) {
      toast.error(language === 'de' 
        ? 'Fehler beim Weiterleiten. Bitte gehen Sie zur Anmeldeseite.' 
        : 'Error redirecting. Please go to the login page.');
    } finally {
      setResending(false);
    }
  };

  const content = {
    de: {
      verifying: {
        title: 'E-Mail wird überprüft...',
        message: 'Bitte warten Sie einen Moment.'
      },
      success: {
        title: '✅ E-Mail erfolgreich bestätigt!',
        message: 'Ihre E-Mail-Adresse wurde erfolgreich verifiziert. Sie können jetzt alle Funktionen von StoryTales nutzen.',
        button: 'Zum Dashboard'
      },
      error: {
        title: '❌ Verifizierung fehlgeschlagen',
        message: 'Der Verifizierungslink ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.',
        button: 'Zur Startseite',
        resendButton: 'Neuen Link anfordern'
      }
    },
    en: {
      verifying: {
        title: 'Verifying email...',
        message: 'Please wait a moment.'
      },
      success: {
        title: '✅ Email verified successfully!',
        message: 'Your email address has been verified. You can now use all StoryTales features.',
        button: 'Go to Dashboard'
      },
      error: {
        title: '❌ Verification failed',
        message: 'The verification link is invalid or expired. Please request a new link.',
        button: 'Go to Home',
        resendButton: 'Request New Link'
      }
    }
  };

  const t = content[language][status] || content.en[status];

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        {status === 'verifying' && (
          <div className="spinner"></div>
        )}
        
        <h1>{t.title}</h1>
        <p>{t.message}</p>
        
        {status !== 'verifying' && (
          <div className="button-group">
            {status === 'error' && (
              <Button 
                onClick={handleResendVerification}
                className="verify-button resend-button"
                disabled={resending}
              >
                {resending ? (language === 'de' ? 'Weiterleitung...' : 'Redirecting...') : t.resendButton}
              </Button>
            )}
            <Button 
              onClick={() => navigate(status === 'success' ? '/dashboard' : '/')}
              className={`verify-button ${status === 'error' ? 'secondary-button' : ''}`}
            >
              {t.button}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
